import { useShallow } from 'zustand/react/shallow';
import type { IvyMarkState } from '@/components/studio/IvyMark';
import { lastCheckpointId } from '@/lib/studio/studioState';
import { isOutOfCreditsMessage } from '@/services/billingPaywallDetector';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import type { UIMessage } from './messageParser';

export interface IvyNow {
  state: IvyMarkState;
  /** Her words so far this turn, in characters: it changes on each streamed chunk. */
  pulse: number;
}

const AGENT_TOOLS = new Set(['Agent', 'Task']);
const ASSEMBLE_TOOLS = new Set(['build_video_manifest', 'build_video_program']);
const shortName = (name = '') => name.replace(/^mcp__[^_]+__/, '');

/**
 * An agent she started this turn is still running: in the foreground until its result, in the background until its
 * notification or until she assembles the video (where the studio ends its builders too). Only this turn's agents
 * count: a notification that never reached the history would otherwise keep her working forever.
 */
function agentRunning(turn: UIMessage[]): boolean {
  const open = new Map<string, boolean>();
  for (const m of turn) {
    if (m.type === 'tool_use' && m.toolUseId && AGENT_TOOLS.has(m.toolName ?? '')) open.set(m.toolUseId, m.toolInput?.run_in_background === true);
    else if (m.type === 'tool_use' && ASSEMBLE_TOOLS.has(shortName(m.toolName))) open.clear();
    else if (m.type === 'tool_result' && m.toolUseId && open.get(m.toolUseId) === false) open.delete(m.toolUseId);
    else if (m.type === 'system' && m.subtype === 'task_notification' && m.toolUseId) open.delete(m.toolUseId);
  }
  return open.size > 0;
}

/**
 * What Ivy is doing, for her mark, from the chat as the store holds it. Her turn is everything after your last
 * message. Waiting on you and a running agent read as the studio's own `waiting` and `working` do.
 */
export function ivyMarkState(messages: UIMessage[], isStreaming: boolean): IvyNow {
  let start = messages.length;
  while (start > 0 && !(messages[start - 1].type === 'chat' && messages[start - 1].role === 'user')) start--;
  const turn = messages.slice(start);
  const said = turn.filter((m) => m.type === 'chat' && m.role === 'assistant');
  const pulse = said.reduce((sum, m) => sum + m.content.length, 0);
  const last = turn.filter((m) => m.type !== 'result').at(-1);

  if (isStreaming) return { state: last?.type === 'chat' && last.role === 'assistant' ? 'streaming' : 'working', pulse };
  if (!last) return { state: 'idle', pulse };
  if (last.type === 'system' && last.subtype === 'error') return { state: 'error', pulse };
  if (isOutOfCreditsMessage(last)) return { state: 'needs-you', pulse };
  if (said.length && lastCheckpointId(said[said.length - 1].content)) return { state: 'needs-you', pulse };
  if (agentRunning(turn)) return { state: 'working', pulse };
  return { state: said.length ? 'done' : 'idle', pulse };
}

/** Ivy's state from the studio chat's store. */
export const useIvyMarkState = (): IvyNow => useEmbeddedIvyChatStore(useShallow((s) => ivyMarkState(s.messages, s.isStreaming)));
